/**
 * The `snapshot:` override: a Flagsmith flag pinned to both halves of what
 * staging served, its `enabled` state and its typed `value`.
 *
 * The one definition of its shape, encoding and validation. Both the e2e list
 * reader (`e2e-flag-defaults.ts`), which writes these, and the override store
 * (`overrides.ts`), which reads them, import it, so the two cannot accept
 * different shapes. It imports nothing, so neither import is a cycle.
 */

export type SnapshotFlagValue = string | number | boolean | null;
export type SnapshotFlagOverride = {
  enabled: boolean;
  value: SnapshotFlagValue;
};

const SNAPSHOT_OVERRIDE_PREFIX = 'snapshot:';

export const isSnapshotFlagOverride = (value: unknown): value is SnapshotFlagOverride => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const snapshot = value as Partial<SnapshotFlagOverride>;
  return (
    typeof snapshot.enabled === 'boolean' &&
    (snapshot.value === null ||
      typeof snapshot.value === 'string' ||
      typeof snapshot.value === 'number' ||
      typeof snapshot.value === 'boolean')
  );
};

export const createSnapshotOverride = (
  snapshot: SnapshotFlagOverride
): `snapshot:${string}` => `${SNAPSHOT_OVERRIDE_PREFIX}${JSON.stringify(snapshot)}`;

/** The pin an override carries, or `undefined` if it is not a valid `snapshot:` one. */
export const getSnapshotOverride = (
  override: string
): SnapshotFlagOverride | undefined => {
  if (!override.startsWith(SNAPSHOT_OVERRIDE_PREFIX)) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(override.slice(SNAPSHOT_OVERRIDE_PREFIX.length));
  } catch {
    return undefined;
  }
  if (!isSnapshotFlagOverride(parsed)) return undefined;
  return { enabled: parsed.enabled, value: parsed.value };
};
